import React, { useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProductListItem } from '@/types';
import { useCartFetchStore } from '@/stores/cartFetchStore';

interface QuantitySelectorProps {
    variation: ProductListItem;
    itemId:number;
    value: number;
    className?: string;
}

export function QuantitySelector({
     variation,
     itemId,
     value,
     className,
 }: QuantitySelectorProps) {
    const [isFocused, setIsFocused] = useState(false);
    const [editValue, setEditValue] = useState('');
    const debounceRef = useRef<number | null>(null);
    const [isDisabledUpdateBtn, setIsDisabledUpdateBtn] =
        useState<boolean>(false);

    const changeQuantity = variation?.packHalfQuantity ?? 1;


    const { updateQuantity} = useCartFetchStore();

    const onUpdate = (delta:number) =>{

        let qty = value + delta;

        if (delta < 0 && value % Math.abs(delta) !== 0) {
            qty = value - (value % Math.abs(delta));
        }

        updateQuantity(
            itemId,
            qty,
            () =>
                setIsDisabledUpdateBtn(
                    true,
                ),
            () =>
                setIsDisabledUpdateBtn(
                    false,
                ),
        ).then()
    }

    const onSet: (value: number) => void= (qty) =>    updateQuantity(itemId, qty)

    // When not focused, display prop value directly (auto-sync)
    // When focused, display local editValue for user input
    const displayValue = isFocused ? editValue : value.toString();

    const triggerChange = (val: number) => {
        if (debounceRef.current) {
            window.clearTimeout(debounceRef.current);
        }

        debounceRef.current = window.setTimeout(() => {
            onSet(Math.max(1, val));
            debounceRef.current = null;
        }, 300);
    };

    const handleFocus = () => {
        setEditValue(value.toString());
        setIsFocused(true);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value;
        setEditValue(rawValue);

        const parsed = parseInt(rawValue, 10);
        if (!isNaN(parsed)) {
            triggerChange(parsed);
        }
    };

    const handleBlur = () => {
        setIsFocused(false);

        // Clear pending debounce and apply final value immediately
        if (debounceRef.current) {
            window.clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }

        const parsed = parseInt(editValue, 10);
        if (!isNaN(parsed) && parsed >= 1) {
            onSet(parsed);
        }
        // If invalid, isFocused becomes false and displayValue reverts to prop value
    };

    return (
        <div className={cn(
            "flex items-center gap-1 bg-zinc-900 rounded-lg p-0.5 border border-zinc-800 transition-colors focus-within:border-brand-gold/30",
            className
        )}>
            <button
                type="button"
                onClick={() => onUpdate(changeQuantity * -1)}
                disabled={isDisabledUpdateBtn || value <= 1}
                className={cn(
                    "flex items-center justify-center rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors disabled:opacity-30",
                    "h-8 w-8 md:h-6 md:w-6",
                )}
            >
                <Minus className="h-4 w-4 md:h-3 md:w-3"/>
            </button>

            <input
                type="number"
                id={"item-q-" + itemId}
                value={displayValue}
                onFocus={handleFocus}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isDisabledUpdateBtn}
                className={cn(
                    "bg-transparent text-center font-bold text-zinc-100 no-spinner focus:outline-none focus:text-brand-gold transition-colors",
                    "w-12 text-sm md:w-8 md:text-xs"
                )}
                min="1"
            />

            <button
                type="button"
                onClick={() => onUpdate(changeQuantity)}
                disabled={isDisabledUpdateBtn}
                className={cn(
                    "flex items-center justify-center rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors disabled:opacity-30",
                    "h-8 w-8 md:h-6 md:w-6",
                )}
            >
                <Plus className="h-4 w-4 md:h-3 md:w-3" />
            </button>
        </div>
    );
}
